import React from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Bell } from "lucide-react";
import useNotifications from "hooks/useNotifications";
import { INotification } from "types/notification";
import { useNavigate } from "react-router-dom";
import notificationRoute from "utills/notificationRoute";

const Notifications = () => {
  const navigate = useNavigate();
  const { data, isLoading, hasNextPage, handleEndReached } = useNotifications();

  return (
    <React.Fragment>
      <BreadCrumb title="Notifications" pageTitle="Notifications" />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading notifications…</div>
        ) : (data?.length ?? 0) === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Bell size={22} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No notifications yet</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table data-stack className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.map((notif) => {
                    // The table previously had no click handler at all, so an
                    // operator reading an alert had no way to act on it.
                    const destination = notificationRoute(notif);

                    return (
                    <tr
                      key={notif._id}
                      className={`transition-colors ${
                        destination
                          ? "hover:bg-gray-50 cursor-pointer"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() => destination && navigate(destination)}
                      // Keyboard parity: a row that acts like a button should
                      // be reachable without a mouse.
                      tabIndex={destination ? 0 : undefined}
                      role={destination ? "button" : undefined}
                      onKeyDown={(e) => {
                        if (destination && (e.key === "Enter" || e.key === " ")) {
                          e.preventDefault();
                          navigate(destination);
                        }
                      }}
                    >
                      <td data-label="Title" data-cell="title" className="px-4 py-3 font-medium text-gray-900">{notif.title}</td>
                      <td data-label="Message" data-cell="block" className="px-4 py-3 text-gray-500">{notif.message}</td>
                      <td data-label="Date Received" className="px-4 py-3 text-gray-500">
                        {new Date(notif.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasNextPage && (
              <div className="flex justify-end px-4 py-3 border-t border-gray-100">
                <button
                  className="text-sm text-indigo-600 hover:underline"
                  onClick={() => handleEndReached()}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default Notifications;
